import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';
import {
  Search,
  X,
  Plus,
  Filter,
  Star,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  XCircle,
  CheckSquare,
  Square,
  ShoppingCart,
  TrendingUp,
  Wrench,
  Palette,
  Settings,
  AlertCircle,
  CheckCircle,
  FileText,
  Sparkles,
} from 'lucide-react';
import { debounce } from 'lodash';
import productService from '../services/productService';
import NoResultsWithAction from './NoResultsWithAction';
import InlineProductCreationForm from './InlineProductCreationForm';
import PriceHistoryModal from './PriceHistoryModal';

const EnhancedProductLookup = ({
  open,
  onClose,
  onAddProducts,
  onPriceApply = null,
  clientName = '',
  clientId = null,
  currentQuotationItems = [],
  mode = 'quotation', // 'quotation', 'outbound', 'inbound'
  availableStock = null, // For outbound: array of {product_id, available_quantity}
  title = '产品查找与选择',
  singleSelect = false, // Single selection mode - select one product and add immediately
  subtitle = '', // Optional subtitle text
  initialSearchQuery = '', // Initial search query to populate
  ocrExtractedData = null, // OCR extracted data for reference
  onOcrSearch = null, // Callback when user clicks "use OCR data to search"
}) => {
  // State management
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState('');
  const searchInputRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    category: '',
    priceMin: '',
    priceMax: '',
    stockStatus: 'all',
    material: '',
    coating: '',
    diameter: '',
    hardness: '',
    specification: '',
    clientRefId: '',
    blade_type: '',
    guide_pillar_length: '',
    guide_pillar_tolerance: '',
    marking: '',
    clientSpecific: false,
    recentlyUsed: false,
  });

  // UI states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(20);

  // Category data (used for filters)
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingCategoryProducts, setLoadingCategoryProducts] = useState(false);

  // Inline product creation state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState({});

  // Price history state
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);

  // Load categories on mount - completely data-driven
  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Get all categories from the API
        const response = await productService.getCategories();
        console.log('Categories API response:', response);

        // Handle different response formats
        // Backend returns: {categories: ["category1", "category2", ...]}
        let categoriesData;
        if (response && Array.isArray(response.categories)) {
          categoriesData = response.categories;
        } else if (Array.isArray(response)) {
          categoriesData = response;
        } else if (response && Array.isArray(response.data)) {
          categoriesData = response.data;
        } else {
          console.error('Unexpected categories response format:', response);
          setCategories([]);
          return;
        }

        if (!categoriesData || categoriesData.length === 0) {
          console.warn('No categories returned from API');
          setCategories([]);
          return;
        }

        // Get product counts for each category in parallel
        const categoriesWithCounts = await Promise.all(
          categoriesData.map(async (categoryName) => {
            if (!categoryName || typeof categoryName !== 'string') {
              console.warn('Invalid category name:', categoryName);
              return null;
            }

            try {
              // Get first page to get total count
              const response = await productService.getProducts(1, 1, 'name', 'asc', { category: categoryName });
              const count = response.pagination?.total_count || response.total_count || 0;

              return {
                id: categoryName, // Use actual category name as ID for exact matching
                name: categoryName, // Use exact name from database
                count: count,
                subcategories: [] // MongoDB categories are flat, no subcategories needed
              };
            } catch (err) {
              console.error(`Failed to get count for category "${categoryName}":`, err);
              // Return category even if count fails, with 0 count so it gets filtered out
              return {
                id: categoryName,
                name: categoryName,
                count: 0,
                subcategories: []
              };
            }
          })
        );

        // Filter out null entries and categories with 0 products, then sort by count desc
        const validCategories = categoriesWithCounts
          .filter(cat => cat && cat.count > 0)
          .sort((a, b) => b.count - a.count);

        setCategories(validCategories);
        setFilteredCategories(validCategories); // Initially show all categories

      } catch (err) {
        console.error('Failed to load categories:', err);
        // Don't set error for categories failure - just log it and continue with empty categories
        // This allows the search functionality to work even if categories fail
        setCategories([]);
        setFilteredCategories([]);
      }
    };

    if (open) {
      loadCategories();
      if (initialSearchQuery) {
        setSearchQuery(initialSearchQuery);
      }
    }
  }, [open, initialSearchQuery]);

  // Load products for a specific category
  const loadCategoryProducts = useCallback(async (categoryName) => {
    try {
      setLoadingCategoryProducts(true);
      setError('');

      // Use the exact category name from database for API call
      const response = await productService.getProducts(1, 50, 'name', 'asc', { category: categoryName });
      let products = response.products || [];

      // Enhance products with additional metadata
      const enhancedProducts = products.map(product => ({
        ...product,
        // Use actual product properties or derive meaningful defaults
        material: product.material || (product.category === '刀具' ? '碳钢' : '钢'),
        coating: product.coating || (product.category === '刀具' ? 'TiN涂层' : '无'),
        diameter: product.diameter || product.specification?.match(/Φ(\d+)/)?.[1],
        length: product.length || product.specification?.match(/L(\d+)/)?.[1],
        hardness: product.hardness || (product.category === '刀具' ? 'HRC62' : ''),
        blade_type: product.blade_type || '',
        guide_pillar_length: product.guide_pillar_length || '',
        guide_pillar_tolerance: product.guide_pillar_tolerance || '',
        marking: product.marking || '',
        lead_time: product.lead_time || '现货',
        min_order_qty: product.min_order_qty || 1,
        unit: product.unit || '支',
        inventory_status: product.inventory_status || '有库存'
      }));

      setCategoryProducts(enhancedProducts);
      setSelectedCategory(categoryName);

    } catch (err) {
      console.error('Failed to load category products:', err);
      setError(`Failed to load products for category "${categoryName}"`);
      setCategoryProducts([]);
    } finally {
      setLoadingCategoryProducts(false);
    }
  }, []);

  // Handle category selection
  const handleCategorySelect = (categoryName) => {
    // Ensure proper state synchronization - both states must be updated together
    setSelectedCategory(categoryName);
    setFilters(prev => ({
      ...prev,
      category: categoryName
    }));

    // Load products for this category
    if (categoryName) {
      loadCategoryProducts(categoryName);
    } else {
      setCategoryProducts([]);
    }

    setActiveTab('search'); // Switch to search tab to show results
    // IMPORTANT: Preserve search query for combined filtering
  };

  // Update filtered categories based on search results
  const updateFilteredCategories = React.useCallback((searchResults, query) => {
    if (!query || query.length < 2) {
      // No search query - show all categories
      setFilteredCategories(categories);
      return;
    }

    // Get categories that appear in search results
    const categoriesInResults = new Map();

    searchResults.forEach(product => {
      if (product.category) {
        const categoryName = product.category;
        if (categoriesInResults.has(categoryName)) {
          categoriesInResults.set(categoryName, categoriesInResults.get(categoryName) + 1);
        } else {
          categoriesInResults.set(categoryName, 1);
        }
      }
    });

    // Filter categories to only show those with search results
    const filtered = categories
      .map(category => {
        const searchResultCount = categoriesInResults.get(category.name) || 0;
        if (searchResultCount > 0) {
          return {
            ...category,
            count: searchResultCount, // Update count to reflect search results
            searchContext: `在搜索结果中找到 ${searchResultCount} 个产品`
          };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count); // Sort by search result count

    setFilteredCategories(filtered);
  }, [categories]);

  // Effect for search with inline debounced handler
  useEffect(() => {
    const handler = debounce(async () => {
      // Show category products if category selected but no search query
      if (selectedCategory && !searchQuery && categoryProducts.length > 0) {
        setSearchResults(categoryProducts);
        updateFilteredCategories(categoryProducts, '');
        return;
      }

      // Skip search if no meaningful criteria
      const activeCategory = selectedCategory || filters.category;
      if (searchQuery.length < 2 && !activeCategory && !Object.values(filters).some(f => f && f !== 'all' && f !== '')) {
        setSearchResults([]);
        setFilteredCategories(categories); // Reset to all categories
        return;
      }

      setLoading(true);
      setError('');

      try {
        let results = [];

        // Add filter criteria - use selectedCategory as the primary source
        const filterParams = {};
        const activeCategoryInner = selectedCategory || filters.category;
        if (activeCategoryInner) {
          filterParams.category = activeCategoryInner; // Use the active category name
        }

        // Perform search with productService
        if (searchQuery.length >= 2) {
          // Use the search endpoint for text-based queries
          const searchResponse = await productService.searchProducts(searchQuery, 1, 50);
          results = searchResponse.products || [];

          // Apply category filtering to search results if category is selected
          if (activeCategoryInner) {
            results = results.filter(product =>
              product.category && product.category.toLowerCase().includes(activeCategoryInner.toLowerCase())
            );
          }
        } else if (activeCategoryInner) {
          // Use the getProducts endpoint with category filter when no search query but category selected
          const getResponse = await productService.getProducts(1, 50, 'name', 'asc', filterParams);
          results = getResponse.products || [];
        } else {
          // No search criteria - return empty results
          results = [];
        }

        // Apply client-side filtering for advanced criteria
        let filteredResults = results;

        // Filter by price range
        if (filters.priceMin || filters.priceMax) {
          filteredResults = filteredResults.filter(product => {
            // Use actual product price if available
            const productPrice = product.price || product.unit_price || 0;
            const min = parseFloat(filters.priceMin) || 0;
            const max = parseFloat(filters.priceMax) || 99999;
            return productPrice >= min && productPrice <= max;
          });
        }

        // Filter by stock status
        if (filters.stockStatus && filters.stockStatus !== 'all') {
          filteredResults = filteredResults.filter(product => {
            const stockStatus = product.inventory_status || product.stock_status || '有库存';
            return (filters.stockStatus === 'in_stock' && stockStatus.includes('有库存')) ||
                   (filters.stockStatus === 'low_stock' && stockStatus.includes('库存不足')) ||
                   (filters.stockStatus === 'out_of_stock' && stockStatus.includes('无库存'));
          });
        }

        // Filter by material
        if (filters.material) {
          filteredResults = filteredResults.filter(product =>
            product.material && product.material.toLowerCase().includes(filters.material.toLowerCase())
          );
        }

        // Filter by coating
        if (filters.coating) {
          filteredResults = filteredResults.filter(product =>
            product.coating && product.coating.toLowerCase().includes(filters.coating.toLowerCase())
          );
        }

        // Filter by diameter
        if (filters.diameter) {
          filteredResults = filteredResults.filter(product =>
            product.diameter && product.diameter.toString().includes(filters.diameter)
          );
        }

        // Filter by hardness
        if (filters.hardness) {
          filteredResults = filteredResults.filter(product =>
            product.hardness && product.hardness.toLowerCase().includes(filters.hardness.toLowerCase())
          );
        }

        // Filter by specification
        if (filters.specification) {
          filteredResults = filteredResults.filter(product =>
            product.specification && product.specification.toLowerCase().includes(filters.specification.toLowerCase())
          );
        }

        // Filter by client reference ID
        if (filters.clientRefId) {
          filteredResults = filteredResults.filter(product =>
            product.client_ref_number && product.client_ref_number.toLowerCase().includes(filters.clientRefId.toLowerCase())
          );
        }

        // Filter by blade type
        if (filters.blade_type) {
          filteredResults = filteredResults.filter(product =>
            product.blade_type && product.blade_type.toLowerCase().includes(filters.blade_type.toLowerCase())
          );
        }

        // Filter by guide pillar length
        if (filters.guide_pillar_length) {
          filteredResults = filteredResults.filter(product =>
            product.guide_pillar_length && product.guide_pillar_length.toString().includes(filters.guide_pillar_length)
          );
        }

        // Filter by guide pillar tolerance
        if (filters.guide_pillar_tolerance) {
          filteredResults = filteredResults.filter(product =>
            product.guide_pillar_tolerance && product.guide_pillar_tolerance.toLowerCase().includes(filters.guide_pillar_tolerance.toLowerCase())
          );
        }

        // Filter by marking
        if (filters.marking) {
          filteredResults = filteredResults.filter(product =>
            product.marking && product.marking.toLowerCase().includes(filters.marking.toLowerCase())
          );
        }

        // Enhance results with additional metadata (mock data for now)
        let enhancedResults = filteredResults.map(product => ({
          ...product,
          // Use actual product properties or derive meaningful defaults
          material: product.material || (product.category === '刀具' ? '碳钢' : '钢'),
          coating: product.coating || (product.category === '刀具' ? 'TiN涂层' : '无'),
          diameter: product.diameter || product.specification?.match(/Φ(\d+)/)?.[1],
          length: product.length || product.specification?.match(/L(\d+)/)?.[1],
          hardness: product.hardness || (product.category === '刀具' ? 'HRC62' : ''),
          blade_type: product.blade_type || '',
          guide_pillar_length: product.guide_pillar_length || '',
          guide_pillar_tolerance: product.guide_pillar_tolerance || '',
          marking: product.marking || '',
          lead_time: product.lead_time || '现货',
          min_order_qty: product.min_order_qty || 1,
          unit: product.unit || '支',
          inventory_status: product.inventory_status || '有库存'
        }));

        // Apply stock filtering for outbound mode
        if (mode === 'outbound' && availableStock) {
          enhancedResults = enhancedResults.map(product => {
            const stockItem = availableStock.find(s =>
              s.product_code === product.product_id ||
              s.product_code === product.product_code
            );
            return {
              ...product,
              available_quantity: stockItem?.remaining_quantity || 0,
              has_stock: stockItem && stockItem.remaining_quantity > 0
            };
          }).filter(product => product.has_stock); // Only show products with stock
        }

        setSearchResults(enhancedResults);

        // Update filtered categories based on search results
        updateFilteredCategories(enhancedResults, searchQuery);

      } catch (err) {
        console.error('Search error:', err);
        setError(err.response?.data?.error || '搜索失败，请重试');
        setSearchResults([]);
        // Reset categories on search error
        setFilteredCategories(categories);
      } finally {
        setLoading(false);
      }
    }, 300);

    handler();
    return () => handler.cancel();
  }, [searchQuery, filters, selectedCategory, categoryProducts, categories, updateFilteredCategories, availableStock, mode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle product selection
  const handleProductSelect = (productId) => {
    if (singleSelect) {
      // In single select mode, find the product and add it immediately
      const product = searchResults.find(p => p._id === productId);
      if (product) {
        onAddProducts([product]);
        onClose();
      }
    } else {
      const newSelected = new Set(selectedProducts);
      if (newSelected.has(productId)) {
        newSelected.delete(productId);
      } else {
        newSelected.add(productId);
      }
      setSelectedProducts(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === searchResults.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(searchResults.map(p => p._id)));
    }
  };

  const handleAddSelected = () => {
    const productsToAdd = searchResults.filter(p => selectedProducts.has(p._id));
    onAddProducts(productsToAdd);
    setSelectedProducts(new Set());
    onClose();
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      priceMin: '',
      priceMax: '',
      stockStatus: 'all',
      material: '',
      coating: '',
      diameter: '',
      hardness: '',
      specification: '',
      clientRefId: '',
      clientSpecific: false,
      recentlyUsed: false,
    });
    setSearchQuery('');
    setSelectedCategory('');
    setCategoryProducts([]);
    setSearchResults([]);
    // Reset categories to show all when clearing filters
    setFilteredCategories(categories);
    setCurrentPage(1);
  };

  // Check if product is already in quotation (by product_id + specification + client_ref_number)
  // Modified: allow duplicate products (same oid) to be added
  const isProductInQuotation = (product) => {
    return false;
  };

  // Handle inline product creation
  const handleCreateProduct = useCallback((initialData = {}) => {
    setCreateFormData({
      ...initialData,
      searchContext: {
        query: searchQuery,
        category: selectedCategory,
        clientName: clientName
      }
    });
    setShowCreateForm(true);
  }, [searchQuery, selectedCategory, clientName]);

  // AI Generate product ID and match
  const handleAIGenerateAndMatch = useCallback(async (ocrData) => {
    if (!ocrData.name) {
      setError('OCR数据缺少产品名称');
      return;
    }

    setAiGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/ai/generate-product-id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: ocrData.name,
          product_specification: ocrData.specification || '',
          material: ''
        })
      });

      const data = await response.json();

      if (data.success && data.product_id) {
        // Search for existing product with this product_id
        const searchResponse = await productService.searchProducts(data.product_id, 1, 5);
        const foundProducts = searchResponse.products || [];

        if (foundProducts.length > 0) {
          // Found existing product - select it
          const foundProduct = foundProducts[0];
          setSearchResults(prev => {
            const filtered = prev.filter(p => p._id !== foundProduct._id);
            return [foundProduct, ...filtered];
          });
          setSearchQuery(data.product_id);
          if (searchInputRef.current) {
            searchInputRef.current.value = data.product_id;
          }
          setActiveTab('results');
          setError('');
        } else {
          // No existing product - create new one with AI-generated ID
          // Set the search query to show the generated product ID
          setSearchQuery(data.product_id);
          if (searchInputRef.current) {
            searchInputRef.current.value = data.product_id;
          }
          
          handleCreateProduct({
            name: ocrData.name,
            product_id: data.product_id,
            specification: ocrData.specification || '',
            tip_diameter: ocrData.tip_diameter || '',
            total_length: ocrData.total_length || ''
          });
        }
      } else {
        setError(data.error || 'AI生成货号失败');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError('AI生成货号失败，请重试');
    } finally {
      setAiGenerating(false);
    }
  }, [handleCreateProduct]);

  const handleProductCreated = async (newProduct) => {
    setShowCreateForm(false);
    setCreateFormData({});

    const newProductIdentifier = newProduct.product_id || newProduct.name;
    if (!newProductIdentifier) {
      setError("无法识别新创建的产品。请手动搜索。");
      return;
    }

    try {
      setLoading(true);
      // Search for the new product to get its full, canonical data from the backend
      const searchResponse = await productService.searchProducts(newProductIdentifier, 1, 5);
      const foundProduct = (searchResponse.products || []).find(p => p.product_id === newProduct.product_id || p.name === newProduct.name);

      if (foundProduct) {
        // Now we have the real product object from the backend

        // Add it to the top of the current search results
        setSearchResults(prev => {
          // Avoid duplicates if it was already in the list from a previous search
          const filtered = prev.filter(p => p._id !== foundProduct._id);
          return [foundProduct, ...filtered];
        });

        // And select it, preserving other selections
        setSelectedProducts(prev => new Set([...prev, foundProduct._id]));

      } else {
        setError(`无法自动刷新新产品 "${newProductIdentifier}"。请手动搜索添加。`);
      }
    } catch (err) {
      console.error("Failed to fetch newly created product", err);
      setError(`获取新产品 "${newProductIdentifier}" 时出错。请手动搜索添加。`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setCreateFormData({});
  };

  // Handle price history - moved up to ensure proper scope
  const handleShowPriceHistory = useCallback((product) => {
    setPriceHistoryProduct(product);
    setShowPriceHistory(true);
  }, []);

  const handleClosePriceHistory = () => {
    setShowPriceHistory(false);
    setPriceHistoryProduct(null);
  };

  const handlePriceSelect = (priceData) => {
    // Apply selected price to the current quotation context
    // This could trigger a callback to update the parent component
    console.log('Selected price:', priceData);

    // For now, we'll just close the modal
    // In a real implementation, this would update the quotation form
    if (onPriceApply) {
      onPriceApply(priceHistoryProduct, priceData);
    }

    handleClosePriceHistory();
  };

  // Pagination
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    return searchResults.slice(startIndex, startIndex + resultsPerPage);
  }, [searchResults, currentPage, resultsPerPage]);

  const totalPages = Math.ceil(searchResults.length / resultsPerPage);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[1400px] w-[90vw] h-[90vh] max-h-[900px] p-0 flex flex-col md:w-[90vw] md:h-[90vh]">
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between border-b px-6 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
            {subtitle && (
              <span className="text-sm text-muted-foreground font-normal">
                {subtitle}
              </span>
            )}
            {clientName && (
              <Badge variant="outline" className="ml-2">
                客户: {clientName}
              </Badge>
            )}
            {mode === 'outbound' && (
              <Badge className="ml-2 bg-amber-500 hover:bg-amber-600">
                仅显示有库存产品
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedProducts.size > 0 && (
              <Badge className="gap-1">
                <ShoppingCart className="h-3 w-3" />
                已选择 {selectedProducts.size} 个产品
              </Badge>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Filters and Navigation (Hidden on mobile) */}
          <div className="hidden md:block w-80 border-r overflow-auto shrink-0">
            {/* Search Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full rounded-none border-b h-12">
                <TabsTrigger value="search" className="flex-1 gap-2">
                  <Search className="h-4 w-4" />
                  搜索
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="p-4">
              {/* Advanced Filters */}
              <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 font-semibold">
                  高级筛选
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform",
                    filtersExpanded && "rotate-180"
                  )} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-2">
                  {/* Category Filter */}
                  <div className="space-y-2">
                    <Label>产品分类</Label>
                    <Select
                      value={selectedCategory || 'all'}
                      onValueChange={(value) => {
                        const categoryName = value === 'all' ? '' : value;
                        if (categoryName) {
                          handleCategorySelect(categoryName);
                        } else {
                          // Clear category selection
                          setSelectedCategory('');
                          setCategoryProducts([]);
                          setFilters(prev => ({ ...prev, category: '' }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="全部分类" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部分类</SelectItem>
                        {filteredCategories.map(cat => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.count})
                            {cat.searchContext && (
                              <span className="ml-1 opacity-70 text-xs">
                                - {cat.searchContext}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Price Range */}
                  <div className="space-y-2">
                    <Label>价格范围</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="最低价"
                        value={filters.priceMin}
                        onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                        className="w-1/2"
                      />
                      <Input
                        type="number"
                        placeholder="最高价"
                        value={filters.priceMax}
                        onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                        className="w-1/2"
                      />
                    </div>
                  </div>

                  {/* Stock Status */}
                  <div className="space-y-2">
                    <Label>库存状态</Label>
                    <Select
                      value={filters.stockStatus}
                      onValueChange={(value) => handleFilterChange('stockStatus', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部</SelectItem>
                        <SelectItem value="in_stock">有库存</SelectItem>
                        <SelectItem value="low_stock">库存不足</SelectItem>
                        <SelectItem value="out_of_stock">无库存</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Manufacturing Specifications */}
                  <div className="space-y-3">
                    <Label className="font-semibold">制造规格</Label>

                    {/* Material Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">材料</Label>
                      <Select
                        value={filters.material || 'all'}
                        onValueChange={(value) => handleFilterChange('material', value === 'all' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="全部材料" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">全部材料</SelectItem>
                          <SelectItem value="硬质合金">硬质合金</SelectItem>
                          <SelectItem value="高速钢">高速钢</SelectItem>
                          <SelectItem value="PCD">PCD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Coating Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">涂层</Label>
                      <Select
                        value={filters.coating || 'all'}
                        onValueChange={(value) => handleFilterChange('coating', value === 'all' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="全部涂层" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">全部涂层</SelectItem>
                          <SelectItem value="无涂层">无涂层</SelectItem>
                          <SelectItem value="普通涂层">普通涂层</SelectItem>
                          <SelectItem value="金刚石涂层">金刚石涂层</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Diameter Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">直径 (mm)</Label>
                      <Input
                        type="number"
                        placeholder="如: 6"
                        value={filters.diameter}
                        onChange={(e) => handleFilterChange('diameter', e.target.value)}
                      />
                    </div>

                    {/* Hardness Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">硬度</Label>
                      <Select
                        value={filters.hardness || 'all'}
                        onValueChange={(value) => handleFilterChange('hardness', value === 'all' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="全部硬度" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">全部硬度</SelectItem>
                          <SelectItem value="HRC60">HRC60</SelectItem>
                          <SelectItem value="HRC62">HRC62</SelectItem>
                          <SelectItem value="HRC65">HRC65</SelectItem>
                          <SelectItem value="HRC67">HRC67</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Specification Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">规格</Label>
                      <Input
                        placeholder="如：M6×1.0×100"
                        value={filters.specification}
                        onChange={(e) => handleFilterChange('specification', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">搜索产品规格描述</p>
                    </div>

                    {/* Client Reference ID Filter */}
                    <div className="space-y-1">
                      <Label className="text-xs">客户参考号</Label>
                      <Input
                        placeholder="如：REF-001"
                        value={filters.clientRefId}
                        onChange={(e) => handleFilterChange('clientRefId', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">搜索客户提供的参考编号</p>
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="clientSpecific"
                        checked={filters.clientSpecific}
                        onCheckedChange={(checked) => handleFilterChange('clientSpecific', checked)}
                      />
                      <label htmlFor="clientSpecific" className="text-sm cursor-pointer">
                        仅显示客户相关产品
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="recentlyUsed"
                        checked={filters.recentlyUsed}
                        onCheckedChange={(checked) => handleFilterChange('recentlyUsed', checked)}
                      />
                      <label htmlFor="recentlyUsed" className="text-sm cursor-pointer">
                        最近使用的产品
                      </label>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="w-full gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    清除筛选
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search Header */}
            <div className="p-4 border-b shrink-0">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder="搜索产品编号、名称、规格、客户参考号、材料或涂层..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchQuery('')}
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* OCR Extracted Data Reference Panel */}
              {ocrExtractedData && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">OCR识别参考</span>
                  </div>
                  <div className="text-xs text-amber-700 space-y-1">
                    {ocrExtractedData.name && <div>名称: {ocrExtractedData.name}</div>}
                    {ocrExtractedData.specification && <div>规格: {ocrExtractedData.specification}</div>}
                    {(ocrExtractedData.quantity || ocrExtractedData.unit) && (
                      <div>数量: {ocrExtractedData.quantity || '-'} {ocrExtractedData.unit || ''}</div>
                    )}
                    {ocrExtractedData.unit_price && (
                      <div>单价: ¥{Number(ocrExtractedData.unit_price).toLocaleString()}</div>
                    )}
                  </div>
                  {onOcrSearch && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => handleAIGenerateAndMatch(ocrExtractedData)}
                      disabled={aiGenerating}
                    >
                      {aiGenerating ? (
                        <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <Sparkles className="h-3 w-3 mr-1" />
                      )}
                      AI生成货号
                    </Button>
                  )}
                </div>
              )}

              {/* Mobile Filters Toggle */}
              <Button
                variant="outline"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="w-full md:hidden gap-2 mb-4"
              >
                <Filter className="h-4 w-4" />
                筛选条件
              </Button>

              {/* Results Header */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    找到 {searchResults.length} 个产品
                  </span>
                  {selectedCategory && (
                    <Badge variant="outline" className="gap-1">
                      分类: {selectedCategory}
                      <button
                        onClick={() => {
                          setSelectedCategory('');
                          setCategoryProducts([]);
                          setFilters(prev => ({ ...prev, category: '' }));
                        }}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {searchQuery && (
                    <Badge variant="secondary" className="gap-1">
                      搜索: "{searchQuery}"
                      <button
                        onClick={() => setSearchQuery('')}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                </div>
                {!singleSelect && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSelectAll}
                      disabled={searchResults.length === 0}
                      className="gap-2"
                    >
                      {selectedProducts.size === searchResults.length ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                      {selectedProducts.size === searchResults.length ? '取消全选' : '全选'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Results Area */}
            <div className="flex-1 overflow-auto p-4">
              {(loading || loadingCategoryProducts) ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : error ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : searchQuery.length < 2 && !selectedCategory && !Object.values(filters).some(f => f && f !== 'all' && f !== '') ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Search className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground mb-2">
                    开始搜索产品
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    请输入产品编号、名称、规格、客户参考号或选择产品分类
                  </p>
                </div>
              ) : paginatedResults.length === 0 ? (
                <>
                  <NoResultsWithAction
                    searchQuery={searchQuery}
                    selectedCategory={selectedCategory}
                    onCreateProduct={handleCreateProduct}
                    showCreateButton={!!searchQuery}
                    clientName={clientName}
                  />

                  {/* Inline Product Creation Form */}
                  <InlineProductCreationForm
                    isOpen={showCreateForm}
                    onClose={handleCloseCreateForm}
                    onProductCreated={handleProductCreated}
                    initialData={createFormData}
                    clientName={clientName}
                    categories={categories}
                  />
                </>
              ) : (
                <>
                  {/* Product Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paginatedResults.map(product => (
                      <ProductCard
                        key={product._id}
                        product={product}
                        isSelected={selectedProducts.has(product._id)}
                        isInQuotation={isProductInQuotation(product)}
                        onSelect={() => handleProductSelect(product._id)}
                        onAddSingle={() => onAddProducts([product])}
                        onShowPriceHistory={handleShowPriceHistory}
                        mode={mode}
                        singleSelect={singleSelect}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="icon"
                              onClick={() => setCurrentPage(pageNum)}
                              className="w-8 h-8"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Action Buttons */}
        <div className="flex items-center justify-between p-4 border-t shrink-0">
          {singleSelect ? (
            <span className="text-sm text-muted-foreground">
              请选择一个产品
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              已选择 {selectedProducts.size} 个产品
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              取消
            </Button>
            {!singleSelect && (
              <Button
                onClick={handleAddSelected}
                disabled={selectedProducts.size === 0}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                添加选中产品 ({selectedProducts.size})
              </Button>
            )}
          </div>
        </div>

        {/* Price History Modal */}
        {priceHistoryProduct && (
          <PriceHistoryModal
            open={showPriceHistory}
            onClose={handleClosePriceHistory}
            initialProductName={priceHistoryProduct.name || ''}
            initialTipDiameter={priceHistoryProduct.tip_diameter || ''}
            initialTotalLength={priceHistoryProduct.total_length || ''}
            initialCategory={priceHistoryProduct.category || ''}
            initialClientName={clientName || ''}
            onPriceSelect={handlePriceSelect}
            mode="quotation"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Product Card Component with improved UI/UX
const ProductCard = ({ product, isSelected, isInQuotation, onSelect, onAddSingle, onShowPriceHistory, mode, singleSelect = false }) => {
  const formatPrice = (price) => {
    return `¥${Number(price).toLocaleString()}`;
  };

  return (
    <Card
      className={cn(
        "relative h-full flex flex-col transition-all duration-200",
        isSelected
          ? "border-2 border-primary bg-primary/5"
          : "border hover:shadow-lg hover:border-primary/50 hover:-translate-y-0.5"
      )}
    >
      <CardContent className="p-4 flex flex-col h-full gap-3">
        {/* Header Row - Selection, ID, Status */}
        <div className="flex justify-between items-start min-h-[32px]">
          <div className="flex items-center gap-3">
            {!singleSelect && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelect}
                className="h-4 w-4"
              />
            )}
            {singleSelect && (
              <div className={cn(
                "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                isSelected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-muted-foreground/30 hover:border-primary"
              )}>
                {isSelected && <CheckCircle className="h-3 w-3" />}
              </div>
            )}
            <div className="bg-primary/10 rounded px-2 py-1 inline-flex items-center h-7">
              <span className="text-sm font-semibold text-primary font-mono tracking-wide">
                {product.product_id}
              </span>
            </div>
            {product.is_favorite && (
              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
            )}
          </div>

          <div className="flex gap-1.5 items-start">
            <Badge variant="secondary" className="text-xs h-7">
              {product.category}
            </Badge>
            {mode === 'outbound' && product.available_quantity !== undefined ? (
              <Badge
                className={cn(
                  "text-xs h-7 font-semibold",
                  product.available_quantity > 10
                    ? "bg-success/100 hover:bg-success"
                    : "bg-amber-500 hover:bg-amber-600"
                )}
              >
                库存: {product.available_quantity}{product.unit || '支'}
              </Badge>
            ) : (product.inventory_status === '有库存' || product.stock_status === '有库存') && (
              <Badge className="text-xs h-7 bg-success/100 hover:bg-success">
                有库存
              </Badge>
            )}
          </div>
        </div>

        {/* Product Title */}
        <div>
          <h3 className="font-semibold text-base leading-tight mb-1">
            {product.name}
          </h3>

          {product.specification && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {product.specification}
            </p>
          )}
        </div>

        {/* Price Section */}
        {product.price && (
          <div className="bg-muted/50 rounded-lg p-3 border">
            <div className="text-lg font-bold text-primary mb-0.5">
              {formatPrice(product.price)}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                / {product.unit || '支'}
              </span>
            </div>

            {/* Lead time or minimum order info */}
            {(product.lead_time || product.min_order_qty > 1) && (
              <p className="text-xs text-muted-foreground">
                {product.lead_time && `交期: ${product.lead_time}`}
                {product.lead_time && product.min_order_qty > 1 && ' • '}
                {product.min_order_qty > 1 && `起订: ${product.min_order_qty}${product.unit || '支'}`}
              </p>
            )}
          </div>
        )}

        {/* Manufacturing Specifications Grid */}
        <div className="flex-1">
          <p className="font-semibold text-sm mb-2">产品规格</p>

          <div className="grid grid-cols-2 gap-2">
            {/* Material */}
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <Wrench className="h-4 w-4 text-primary/70" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground leading-tight">材料</p>
                <p className="text-sm font-medium leading-tight">{product.material || '碳钢'}</p>
              </div>
            </div>

            {/* Coating */}
            {product.coating && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Palette className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">涂层</p>
                  <p className="text-sm font-medium leading-tight">{product.coating}</p>
                </div>
              </div>
            )}

            {/* Diameter */}
            {product.diameter && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">直径</p>
                  <p className="text-sm font-medium leading-tight">Φ{product.diameter}mm</p>
                </div>
              </div>
            )}

            {/* Hardness */}
            {product.hardness && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">硬度</p>
                  <p className="text-sm font-medium leading-tight">{product.hardness}</p>
                </div>
              </div>
            )}

            {/* Blade Type */}
            {product.blade_type && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">刃型</p>
                  <p className="text-sm font-medium leading-tight">{product.blade_type}</p>
                </div>
              </div>
            )}

            {/* Guide Pillar Length */}
            {product.guide_pillar_length && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">导柱长度</p>
                  <p className="text-sm font-medium leading-tight">{product.guide_pillar_length}mm</p>
                </div>
              </div>
            )}

            {/* Guide Pillar Tolerance */}
            {product.guide_pillar_tolerance && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">导柱公差</p>
                  <p className="text-sm font-medium leading-tight">{product.guide_pillar_tolerance}</p>
                </div>
              </div>
            )}

            {/* Marking */}
            {product.marking && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">打标</p>
                  <p className="text-sm font-medium leading-tight">{product.marking}</p>
                </div>
              </div>
            )}

            {/* Client Reference Number */}
            {product.client_ref_number && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">物料编码</p>
                  <p className="text-sm font-medium leading-tight">{product.client_ref_number}</p>
                </div>
              </div>
            )}

            {/* Created Date */}
            {product.created_at && (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-primary/70" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground leading-tight">创建日期</p>
                  <p className="text-sm font-medium leading-tight">
                    {new Date(product.created_at).toLocaleDateString('zh-CN', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center mt-auto">
          {isInQuotation ? (
            <Button
              variant="outline"
              className="flex-1 text-success border-green-300"
              disabled
            >
              ✓ 已在报价单中
            </Button>
          ) : (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onShowPriceHistory(product)}
                      className="shrink-0"
                    >
                      <TrendingUp className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>查看价格历史</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                onClick={onAddSingle}
                className="flex-1 gap-2"
              >
                <Plus className="h-4 w-4" />
                添加到销售订单
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedProductLookup;
